// The transcript of a conversation as staff read it — shared by the conversation page and the sandbox page.
import { useTranslation } from 'react-i18next';
import type { TranscriptMessage } from '../../types/api';
import { ToolCallDetail } from './ToolCallDetail';

/** Messages grouped by turn, so the ratings and signals of a turn sit beside it. */
export function turns(messages: TranscriptMessage[]): Map<number, TranscriptMessage[]> {
  const out = new Map<number, TranscriptMessage[]>();
  for (const m of messages) out.set(m.turn_index, [...(out.get(m.turn_index) ?? []), m]);
  return out;
}

/** One assistant message: its reasoning and plan (folded), its text, each tool call (folded), the model and tokens. */
export function AgentMessage({ m, open }: { m: TranscriptMessage; open: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5">
      {!!m.thinking?.length && (
        <details open={open} className="rounded-sm border border-line-soft px-2 py-1 text-[12px] text-muted">
          <summary className="cursor-pointer font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t('quality.reasoning')}</summary>
          {m.thinking.map((th, i) => (
            <p key={i} className="mt-1 whitespace-pre-wrap wrap-break-word">{th}</p>
          ))}
        </details>
      )}
      {m.content && <p className="whitespace-pre-wrap wrap-break-word text-[13px] text-ink">{m.content}</p>}
      {m.plan && (
        <details open={open} className="rounded-sm border border-line-soft bg-accent-bg px-2 py-1 text-[12px]" data-testid="plan">
          <summary className="cursor-pointer font-mono text-[11px] text-accent-dark">
            {t('quality.plan')}: {[m.plan.operation, m.plan.name].filter(Boolean).join(' ')}
            {typeof m.plan.calls === 'number' && ` · ${t('quality.planCalls', { count: m.plan.calls })}`}
          </summary>
          <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-ink">
            {m.plan.markdown ?? JSON.stringify(m.plan, null, 2)}
          </pre>
        </details>
      )}
      {(m.tool_calls ?? []).map((c) => (
        <ToolCallDetail key={c.tool_call_id} call={c} open={open} />
      ))}
      {(m.model || m.usage) && (
        <div className="wrap-break-word font-mono text-[10.5px] text-fainter">
          {[m.model, m.usage && t('quality.tokens', { input: m.usage.input_tokens ?? 0, output: m.usage.output_tokens ?? 0 })].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}
